import * as React from 'react';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import type {DropResult} from 'react-beautiful-dnd';

import classnames from 'classnames';

import {DraggableRow} from 'shells/draggable-row/draggable-row';
import {DraggableRowOnHover} from 'shells/draggable-row/draggable-row-on-hover';

import styles from './custom-fields.module.css';

export const SIDEBAR_BUCKET_ID = '1';
export const TOP_THREE_BUCKET_ID = '0';
export const TOP_THREE_BUCKET_AMOUNT = 3;

type ListItem = {
    id: string;
    component: React.ReactNode;
    bucketId: string;
    isActive?: boolean;
};

type Props = {
    listItems: ListItem[];
    handleReorder: (
        startIndex: number,
        endIndex: number,
        sourceBucketId: string,
        destinationBucketId: string,
        stagesetId: string
    ) => void;
    showDraggableRowOnlyOnHover?: boolean;
    stagesetId: string;
};

export const CustomFieldsPipelineDraggableList = (props: Props) => {
    const [draggedItemId, setDraggedItemId] = React.useState<string | null | undefined>(null);
    const [hoveredItemId, setHoveredItemId] = React.useState<string | null | undefined>(null);

    const buckets = [
        {
            id: '0',
            title: 'TOP CUSTOM FIELDS',
            emptyState: 'Drag and drop your most important fields here',
        },
        {
            id: '1',
            title: 'STANDARD CUSTOM FIELDS',
            emptyState: 'Create a lead custom field and it will be defaulted here',
        },
        {
            id: '2',
            title: 'FIELDS HIDDEN FROM THIS PIPELINE',
            emptyState: 'Fields not assigned to this pipeline will be listed here',
        },
    ];

    // @ts-expect-error
    const shouldShowEmptyState = (bucketId) => {
        return (
            (bucketId === TOP_THREE_BUCKET_ID && centerBucketCount < TOP_THREE_BUCKET_AMOUNT) ||
            (bucketId !== TOP_THREE_BUCKET_ID &&
                props.listItems.filter((item) => item.bucketId === bucketId).length === 0)
        );
    };

    const getDraggingStyle = (isDragging: boolean, draggableStyle: any) => {
        const {left, top, ...restStyles} = draggableStyle;

        let newTop;
        if (!isNaN(top)) {
            newTop = top;
        }

        const draggingStyle = {
            margin: '0 0 8px 0',
            left: left,
            top: newTop ? newTop - 50 : top,
            ...restStyles,
        } as const;

        if (draggableStyle.transform) {
            const axisLockY = `translate(0px${draggableStyle.transform.slice(
                draggableStyle.transform.indexOf(','),
                draggableStyle.transform.length
            )}`;

            return {
                ...draggingStyle,
                transform: axisLockY,
            };
        }

        return draggingStyle;
    };

    const centerBucketCount = props.listItems.filter(
        (item) => item.bucketId === TOP_THREE_BUCKET_ID
    ).length;

    const handleDragEnd = (result: DropResult) => {
        setDraggedItemId(null);
        if (!result.destination) return;

        props.handleReorder(
            result.source.index,
            result.destination.index,
            result.source.droppableId,
            result.destination.droppableId,
            props.stagesetId
        );
    };

    return (
        <DragDropContext
            onDragEnd={handleDragEnd}
            onDragStart={(e) => {
                setDraggedItemId(e.draggableId);
            }}
        >
            <div>
                {buckets.map((bucket) => (
                    <div key={bucket.id}>
                        <div className='font-bold mt-16'>
                            {bucket.id === TOP_THREE_BUCKET_ID
                                ? `${bucket.title} (${centerBucketCount}/${TOP_THREE_BUCKET_AMOUNT})`
                                : bucket.title}
                        </div>
                        <Droppable droppableId={bucket.id} isDropDisabled={false}>
                            {(droppableProvided) => (
                                <div
                                    ref={droppableProvided.innerRef}
                                    {...droppableProvided.droppableProps}
                                    className={classnames(
                                        'flex flex-dir-col my-8',
                                        // @ts-expect-error
                                        droppableProvided.droppableProps?.className
                                    )}
                                >
                                    <div
                                        className={
                                            bucket.id === TOP_THREE_BUCKET_ID
                                                ? styles['pipeline-bucket-content-center']
                                                : styles['pipeline-bucket-content']
                                        }
                                    >
                                        {props.listItems
                                            .filter((item) => item.bucketId === bucket.id)
                                            .map((item, index) => (
                                                <Draggable
                                                    key={item.id}
                                                    index={index}
                                                    draggableId={item.id}
                                                >
                                                    {(draggableProvided, draggableSnapshot) => {
                                                        const style = getDraggingStyle(
                                                            draggableSnapshot.isDragging,
                                                            draggableProvided.draggableProps.style
                                                        );

                                                        return (
                                                            <div
                                                                ref={draggableProvided.innerRef}
                                                                {...draggableProvided.draggableProps}
                                                                style={style}
                                                            >
                                                                {props.showDraggableRowOnlyOnHover ? (
                                                                    <DraggableRowOnHover
                                                                        dragHandleProps={{
                                                                            ...draggableProvided.dragHandleProps,
                                                                        }}
                                                                        disableHover={
                                                                            Boolean(
                                                                                draggedItemId
                                                                            ) &&
                                                                            draggedItemId !==
                                                                                draggableProvided
                                                                                    .draggableProps[
                                                                                    'data-rbd-draggable-id'
                                                                                ]
                                                                        }
                                                                        isActive={
                                                                            item.isActive ||
                                                                            draggableSnapshot.isDragging
                                                                        }
                                                                        onHoverBegin={() => {
                                                                            setHoveredItemId(
                                                                                item.id
                                                                            );
                                                                        }}
                                                                        onHoverEnd={() => {
                                                                            setHoveredItemId(null);
                                                                        }}
                                                                        isHovered={
                                                                            item.id ===
                                                                            hoveredItemId
                                                                        }
                                                                    >
                                                                        {item.component}
                                                                    </DraggableRowOnHover>
                                                                ) : (
                                                                    <DraggableRow
                                                                        dragHandleProps={
                                                                            draggableProvided.dragHandleProps
                                                                        }
                                                                    >
                                                                        {item.component}
                                                                    </DraggableRow>
                                                                )}
                                                                {/* @ts-expect-error */}
                                                                {draggableProvided.placeholder}
                                                            </div>
                                                        );
                                                    }}
                                                </Draggable>
                                            ))}
                                        {shouldShowEmptyState(bucket.id) && (
                                            <div className='background-offwhite py-8 px-16 text-align-center flex flex-grow align-center justify-center'>
                                                {bucket.emptyState}
                                            </div>
                                        )}
                                        {droppableProvided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    </div>
                ))}
            </div>
        </DragDropContext>
    );
};
